// DEV / QA ONLY -- never part of a production build.
// Mounted at /__brand-qa only when the app is built/started with
// REACT_APP_BRAND_QA=true (see App.js). It renders the real brand-layer
// components and the real shared state primitives side by side so the visual
// system can be reviewed and screenshotted as a whole. No data, no API calls.

import { GLIM, GLIMState, ARC, IntelligenceChain, MartianLockup, MartianWordmark, MartianEndCard, GLIM_STATE_IDS, GLIM_ASSETS } from '@/brand';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { EmptyState as FounderEmpty, Loading as FounderLoading } from '@/founder/ui';
import MartianLearningState from '@/components/brand/MartianLearningState';

const Section = ({ id, title, children, dark = false }) => (
  <section id={id} data-testid={`qa-${id}`} className={`rounded-[var(--radius-card)] border border-[var(--border)] p-6 mb-6 ${dark ? 'bg-[var(--brand-ink)] text-[var(--brand-cream)]' : 'bg-[var(--surface)]'}`}>
    <h2 className={`text-[12px] font-mono uppercase tracking-wide mb-4 ${dark ? 'text-[var(--brand-cream)]' : 'text-[var(--text-tertiary)]'}`}>{title}</h2>
    {children}
  </section>
);

export default function BrandQAPage() {
  return (
    <div className="max-w-5xl mx-auto p-6 bg-[var(--canvas-secondary)] min-h-screen" data-testid="brand-qa-page">
      <Section id="wordmark" title="Wordmark + lockups (light / dark)">
        <div className="flex flex-wrap items-center gap-10">
          <MartianWordmark size="xl" />
          <MartianLockup size="lg" showDescriptor />
          <MartianLockup size="lg" layout="stacked" showDescriptor />
        </div>
        <div className="mt-6 rounded-lg bg-[var(--brand-ink)] p-6 flex flex-wrap items-center gap-10">
          <MartianLockup size="lg" tone="dark" showDescriptor />
          <MartianLockup size="md" tone="dark" layout="stacked" showDescriptor />
        </div>
      </Section>

      <Section id="glim-ladder" title="GLIM optical size ladder (16 / 24 / 32 / 48 / 96 / 160)">
        <div className="flex flex-wrap items-end gap-6">
          {[16, 24, 32, 48, 96, 160].map((s) => <GLIM key={s} size={s} decorative />)}
        </div>
      </Section>

      <Section id="glim-states" title="GLIM — 15 approved product states">
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-4">
          {GLIM_STATE_IDS.map((id) => (
            <GLIMState key={id} state={id} size={96} showLabel label={id.replace(/_/g, ' ')} layout="stacked" motion={false} />
          ))}
        </div>
      </Section>

      <Section id="arc" title="ARC — system language (light)">
        <div className="flex items-center gap-8 mb-6"><ARC size={96} /><ARC size={48} /><ARC size={32} /></div>
        <IntelligenceChain />
        <div className="mt-8 max-w-xl"><IntelligenceChain compact activeKey="recommendation" states={{ signal: 'complete', diagnosis: 'complete', opportunity: 'complete', recommendation: 'active', action: 'pending', measurement: 'pending', learning: 'pending' }} /></div>
        <p className="mt-8 text-[11px] font-mono uppercase text-[var(--text-tertiary)]">Product Truth: vision / unsupported stages</p>
        <div className="mt-3"><IntelligenceChain truth={{ signal: 'live', diagnosis: 'live', opportunity: 'live', recommendation: 'live', action: 'vision', measurement: 'unsupported' }} /></div>
      </Section>

      <Section id="arc-dark" title="ARC — Intelligence Chain (dark)" dark>
        <IntelligenceChain tone="dark" />
      </Section>

      <Section id="console-states" title="Console states — loading / empty / error (shared primitives)">
        <div className="grid sm:grid-cols-3 gap-4">
          <LoadingState />
          <EmptyState title="Nothing here yet" description="Martian has no evidence for this yet." whyItMatters="Connect a source to begin." />
          <ErrorState error={new Error('Network down')} onRetry={() => {}} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <FounderLoading label="Opening the decision…" />
          <FounderEmpty title="No decisions waiting" body="When Martian finds something that needs you, it will appear here." />
        </div>
      </Section>

      <Section id="approval" title="Approval Required — Martian recommends. Human approves where required.">
        <div className="flex flex-wrap items-center gap-3">
          <GLIMState state="approval_required" size={56} showDescription />
          <button type="button" className="h-10 px-5 rounded-[var(--radius-button)] bg-[var(--accent)] text-white text-[14px] font-semibold">Approve</button>
          <button type="button" className="h-10 px-5 rounded-[var(--radius-button)] border border-[var(--border)] text-[14px]">Edit</button>
        </div>
      </Section>

      <Section id="learning" title="Learning state">
        <MartianLearningState brandLabel="Demo brand" companyContextSummaryText={null} promptsCount={0} checkInProgress={false} onAskMartian={() => {}} onReviewCompanyContext={() => {}} />
      </Section>

      <Section id="icons" title="App surfaces — favicon / app icon / PWA / social / OG">
        <div className="flex flex-wrap items-end gap-6">
          {[GLIM_ASSETS.icons.favicon16, GLIM_ASSETS.icons.favicon32, GLIM_ASSETS.icons.favicon48, GLIM_ASSETS.icons.appleTouch180, GLIM_ASSETS.icons.pwa192, GLIM_ASSETS.icons.socialAvatar].map((src) => (
            <img key={src} src={src} alt="" style={{ imageRendering: 'auto' }} />
          ))}
        </div>
        <img src={GLIM_ASSETS.icons.og} alt="OG share card" className="mt-6 w-full max-w-xl rounded-lg border border-[var(--border)]" />
      </Section>

      <MartianEndCard className="rounded-[28px] px-6 py-14" />
    </div>
  );
}
